import React, { useState } from 'react';
import { useSolarStore } from '../../store/useSolarStore';
import { Eye, UploadCloud, ShieldCheck, CheckCircle2, AlertTriangle, ArrowRight, Camera, Sparkles, Building2, Sun } from 'lucide-react';

export const RoofAnalyzer: React.FC = () => {
  const store = useSolarStore();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [sampleType, setSampleType] = useState<'corrugated' | 'flat_concrete' | 'shaded'>('corrugated');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<{
    orientation: string;
    shadingRisk: string;
    roofMaterial: string;
    safetyScore: number;
    feasibilityRating: string;
    recommendedPanelCount: number;
    recommendedCapacityKw: number;
    engineeringNotes: string;
  } | null>(null);

  const sampleImages = [
    {
      id: 'corrugated',
      label: 'Longspan Aluminum Roof',
      type: 'corrugated',
      url: 'https://images.unsplash.com/photo-1508873696983-2df515122519?auto=format&fit=crop&w=600&q=80',
    },
    {
      id: 'flat_concrete',
      label: 'Concrete Flat Roof Deck',
      type: 'flat_concrete',
      url: 'https://images.unsplash.com/photo-1613665813446-82a78c468a1d?auto=format&fit=crop&w=600&q=80',
    },
    {
      id: 'shaded',
      label: 'Tree Overhang Shaded Tile',
      type: 'shaded',
      url: 'https://images.unsplash.com/photo-1592833159057-65142723304a?auto=format&fit=crop&w=600&q=80',
    },
  ];

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setSelectedImage(base64);
        analyzeRoofImage(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const analyzeRoofImage = async (base64Img?: string, type?: 'corrugated' | 'flat_concrete' | 'shaded') => {
    setLoading(true);
    try {
      const res = await fetch('/api/ai/analyze-roof', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageBase64: base64Img || selectedImage || '',
          sampleType: type || sampleType,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setAnalysis(data);
      }
    } catch (err) {
      console.error("Roof Vision Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const getFeasibilityBadge = (rating: string) => {
    switch (rating) {
      case 'EXCELLENT':
        return <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 text-xs font-black px-3 py-1 rounded-full">EXCELLENT FEASIBILITY</span>;
      case 'GOOD':
        return <span className="bg-amber-500/20 text-amber-300 border border-amber-500/40 text-xs font-black px-3 py-1 rounded-full">GOOD FEASIBILITY</span>;
      case 'STRUCTURAL_FRAMING_REQUIRED':
        return <span className="bg-orange-500/20 text-orange-300 border border-orange-500/40 text-xs font-black px-3 py-1 rounded-full">STRUCTURAL RACKING NEEDED</span>;
      default:
        return <span className="bg-red-500/20 text-red-400 border border-red-500/40 text-xs font-black px-3 py-1 rounded-full">POOR FEASIBILITY</span>;
    }
  };

  return (
    <div className="bg-slate-900/90 border border-slate-800 rounded-3xl p-6 md:p-8 shadow-2xl relative overflow-hidden font-sans">
      <div className="absolute -top-10 -left-10 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-emerald-500/20 border border-emerald-500/30 rounded-2xl text-emerald-400">
          <Eye className="w-6 h-6" />
        </div>
        <div>
          <h2 className="text-xl font-black text-slate-100 flex items-center gap-2">
            AI Computer Vision Roof & Shading Feasibility Analysis
            <span className="bg-emerald-500/20 text-emerald-300 text-xs font-bold px-2.5 py-0.5 rounded-full border border-emerald-500/30">
              VISION AI
            </span>
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Upload a photo of your building or roof. Gemini Vision inspects tilt angle, tree shading, material integrity, and panel capacity.
          </p>
        </div>
      </div>

      {/* Upload & Sample Selector Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        {/* Upload Box */}
        <div className="lg:col-span-5 flex flex-col justify-center border-2 border-dashed border-slate-700 hover:border-emerald-500/80 rounded-2xl p-6 text-center bg-slate-950/60 transition-all relative cursor-pointer group">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          />
          <div className="flex flex-col items-center justify-center">
            <div className="p-4 bg-slate-800/80 rounded-2xl group-hover:bg-emerald-500/20 group-hover:text-emerald-400 transition-colors mb-3">
              <UploadCloud className="w-8 h-8 text-slate-400 group-hover:text-emerald-400" />
            </div>
            <div className="text-sm font-bold text-slate-200">Upload Building or Roof Photo</div>
            <div className="text-xs text-slate-500 mt-1">PNG, JPG, WEBP up to 10MB</div>
            <button className="mt-4 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-xs font-bold px-4 py-2 rounded-xl flex items-center gap-2">
              <Camera className="w-4 h-4" /> Take or Browse Photo
            </button>
          </div>
        </div>

        {/* Sample Roof Presets */}
        <div className="lg:col-span-7 flex flex-col justify-between">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Or Select a Sample Building Roof Profile:</span>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {sampleImages.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  setSampleType(s.type as any);
                  setSelectedImage(s.url);
                  analyzeRoofImage(s.url, s.type as any);
                }}
                className={`group relative rounded-xl overflow-hidden border-2 text-left transition-all ${
                  sampleType === s.type
                    ? 'border-emerald-500 ring-2 ring-emerald-500/30'
                    : 'border-slate-800 hover:border-slate-700'
                }`}
              >
                <img src={s.url} alt={s.label} className="w-full h-24 object-cover group-hover:scale-105 transition-transform" />
                <div className="p-2 bg-slate-900/90 text-[11px] font-bold text-slate-200 border-t border-slate-800">
                  {s.label}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Selected Image Preview & Loader */}
      {loading && (
        <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-8 text-center flex flex-col items-center justify-center">
          <Eye className="w-10 h-10 text-emerald-400 animate-pulse mb-3" />
          <div className="text-sm font-bold text-slate-200">Gemini Vision AI Inspecting Roof Geometry & Shading...</div>
          <div className="text-xs text-slate-500 mt-1">Calculating usable panel surface area, pitch, and structural safety score.</div>
        </div>
      )}

      {/* Analysis Output Dashboard */}
      {analysis && !loading && (
        <div className="bg-slate-950/90 border border-slate-800 rounded-2xl p-6 space-y-6 animate-in fade-in duration-300">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
            <div>
              <div className="text-xs text-slate-400 uppercase tracking-wider font-bold">COREN Engineering Assessment</div>
              <div className="text-lg font-black text-slate-100 flex items-center gap-3 mt-1">
                {analysis.roofMaterial}
              </div>
            </div>
            <div>{getFeasibilityBadge(analysis.feasibilityRating)}</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-slate-900 p-4 rounded-xl border border-slate-800">
              <span className="text-xs text-slate-400 font-medium">Usable Orientation</span>
              <div className="text-sm font-black text-amber-400 mt-1 flex items-center gap-1.5">
                <Sun className="w-4 h-4 text-amber-400" />
                {analysis.orientation}
              </div>
            </div>

            <div className="bg-slate-900 p-4 rounded-xl border border-slate-800">
              <span className="text-xs text-slate-400 font-medium">Shading Risk</span>
              <div className="text-sm font-black text-emerald-400 mt-1">
                {analysis.shadingRisk}
              </div>
            </div>

            <div className="bg-slate-900 p-4 rounded-xl border border-slate-800">
              <span className="text-xs text-slate-400 font-medium">Structural Safety Score</span>
              <div className="text-xl font-black text-emerald-400 mt-1">
                {analysis.safetyScore} / 100
              </div>
            </div>

            <div className="bg-slate-900 p-4 rounded-xl border border-slate-800">
              <span className="text-xs text-slate-400 font-medium">Max Panel Capacity</span>
              <div className="text-xl font-black text-amber-400 mt-1">
                {analysis.recommendedPanelCount} Panels ({analysis.recommendedCapacityKw} kWp)
              </div>
            </div>
          </div>

          {/* Detailed Engineering Notes */}
          <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 text-xs text-slate-300 leading-relaxed">
            <div className="font-bold text-slate-100 mb-1 flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-400" /> Engineering Field Notes:
            </div>
            {analysis.engineeringNotes}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
            <div className="text-xs text-slate-400">
              Want our senior solar engineers to verify this roof layout physically on site?
            </div>
            <button
              onClick={() => store.setIsLeadModalOpen(true, `Roof Vision Audit (${analysis.roofMaterial})`)}
              className="w-full sm:w-auto bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs px-6 py-3.5 rounded-xl shadow-lg shadow-amber-500/20 transition-all flex items-center justify-center gap-2"
            >
              Attach Analysis & Request Physical Audit <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
